import { useCallback, useEffect, useRef, useState } from 'react';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  fetchProfileLeadQueue,
  resendProfileLeadEmail,
  type ProfileLeadEmailType,
  type ProfileLeadQueueResult
} from '@/services/profileLeadQueueService';

export const PROFILE_LEAD_ONLINE_LEADS_FLAG = 'profile_lead_online_leads_v1';

/** Website messages for Artemis Online Leads, behind their own flag. */
export function useProfileLeadQueue() {
  const gate = useFeatureFlag(PROFILE_LEAD_ONLINE_LEADS_FLAG);
  const [result, setResult] = useState<ProfileLeadQueueResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const request = useRef(0);
  const enabled = !gate.loading && gate.enabled;
  const load = useCallback(async () => {
    if (!enabled) {
      return;
    }
    const id = ++request.current;
    setBusy(true);
    setError(null);
    try {
      const next = await fetchProfileLeadQueue();
      if (id === request.current) {
        setResult(next);
      }
    } catch (failure) {
      if (id === request.current) {
        setResult(null);
        setError(failure instanceof Error ? failure.message : 'Unable to load website leads.');
      }
    } finally {
      if (id === request.current) {
        setBusy(false);
      }
    }
  }, [enabled]);
  useEffect(() => {
    const counter = request;
    if (enabled) {
      void load();
    } else {
      setResult(null); setError(null); setBusy(false);
    }
    return () => {
      ++counter.current;
    };
  }, [enabled, load]);
  const resend = async (submissionId: string, notificationType: ProfileLeadEmailType) => {
    if (!enabled || !result?.canResend) {
      return;
    }
    await resendProfileLeadEmail(submissionId, notificationType);
    await load();
  };
  // Answered: the flag resolved, and when it is on, the server answered (a
  // transport error is not an answer; Refresh may still load the lead).
  const answered = !gate.loading && (!gate.enabled || (!busy && result !== null));
  // Hide retained state synchronously when a flag changes, before effect cleanup.
  return {
    result: enabled ? result : null,
    error: enabled ? error : null,
    busy: enabled && busy,
    answered,
    load,
    resend
  };
}
